import React, { HTMLAttributes } from "react";
import { useTitle } from "../lib/title";
import { useLogError } from "../lib/logging";

export function RouteNotFoundErrorPage() {
    useTitle("Page Not Found");
    return <embedded-error-page src="/error-pages/not-found.html" />;
}

export function ErrorPage() {
    useTitle("Error");
    return <embedded-error-page src="/error-pages/error-500.html" />;
}

export function BadRequestErrorPage() {
    useTitle("Bad Request");
    return <embedded-error-page src="/error-pages/bad-request.html" />;
}

export function ForbiddenErrorPage() {
    useTitle("Forbidden");
    return <embedded-error-page src="/error-pages/forbidden.html" />;
}

export function ForbiddenPowerUserErrorPage() {
    useTitle("Forbidden Power User");
    return <embedded-error-page src="/error-pages/forbidden-poweruser.html" />;
}

export function SelectUserPage() {
    // not really an error page, but displays the same way
    useTitle("Select Customer");

    // log the user out of Grafana
    // otherwise they won't be re-authenticated when they switch users
    // don't follow the redirect as it will try and re-authenticate again
    const logError = useLogError();
    fetch("/grafana/logout", { redirect: "manual" }).catch((error) => {
        logError({
            subject: "Failed to logout of Grafana",
            error: error,
        });
    });

    return (
        <embedded-error-page
            src="/error-pages/select-user.html"
            data-testid="select-user-page"
        />
    );
}

class EmbeddedErrorPage extends HTMLElement {
    connectedCallback() {
        const src = this.getAttribute("src");
        if (!src) {
            throw Error(
                "An EmbeddedErrorPage must have a specified src attribute"
            );
        }
        fetch(src)
            .then((response) => {
                return response.text();
            })
            .then((html) => {
                // Base elements don't work in web components, so we have to re-write the urls
                html = html.replaceAll('src="', 'src="/error-pages/');
                // Make sure to not rewrite mailto links
                html = html.replaceAll(
                    /href="(?!mailto)/g,
                    'href="/error-pages/'
                );
                // It's not a 404, but a not found route.
                html = html.replaceAll(/HTTP \d+ -/gi, "");
                const shadow = this.attachShadow({ mode: "closed" });
                shadow.innerHTML = html;
            });
    }
}

customElements.define("embedded-error-page", EmbeddedErrorPage);

declare global {
    namespace JSX {
        interface IntrinsicElements {
            "embedded-error-page": EmbeddedErrorPageAttributes;
        }
        interface EmbeddedErrorPageAttributes
            extends HTMLAttributes<HTMLElement> {
            src: string;
        }
    }
}
